import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Smartphone, X } from 'lucide-react';
import { AppleIcon, GooglePlayIcon } from './StoreIcons';
import { APP_STORES } from '../config/appStores';

export function AppDownloadPrompt({ suppressed = false }: { suppressed?: boolean }) {
  const [mode, setMode] = useState<'teaser' | 'expanded' | 'collapsed' | 'dismissed'>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches ? 'teaser' : 'expanded');
  const [size, setSize] = useState({ width: 0, height: 0 });
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const teaserRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocus = useRef(false);
  const reducedMotion = useReducedMotion();
  const collapsed = mode === 'collapsed';
  const teaser = mode === 'teaser';
  const compact = collapsed || teaser;
  const shown = mode !== 'dismissed' && !suppressed;
  const restoreControlFocus = useCallback(() => {
    if (!shown || !restoreFocus.current) return;
    const button = collapsed ? launcherRef.current : teaser ? teaserRef.current : closeRef.current;
    button?.focus({ preventScroll: true });
    if (document.activeElement === button) restoreFocus.current = false;
  }, [collapsed, teaser, shown]);

  useLayoutEffect(() => {
    const widget = widgetRef.current;
    const content = contentRef.current;
    if (!shown || !widget || !content) return;
    // Measure the full content independently so text never reflows while shrinking.
    const measure = () => {
      const width = widget.clientWidth;
      const height = content.offsetHeight;
      setSize((previous) => previous.width === width && previous.height === height
        ? previous : { width, height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(widget);
    observer.observe(content);
    return () => observer.disconnect();
  }, [shown]);

  useLayoutEffect(restoreControlFocus, [restoreControlFocus]);

  useEffect(() => {
    if ((mode !== 'expanded' && mode !== 'teaser') || suppressed) return;
    let scrollStart = Math.max(0, window.scrollY);
    const collapseOnScroll = () => {
      const scrollY = Math.max(0, window.scrollY);
      scrollStart = Math.min(scrollStart, scrollY);
      // A small threshold ignores touch jitter; reopening starts a new interval.
      if (scrollY - scrollStart >= 24) {
        restoreFocus.current = !!panelRef.current?.contains(document.activeElement);
        setMode('collapsed');
      }
    };
    window.addEventListener('scroll', collapseOnScroll, { passive: true });
    return () => window.removeEventListener('scroll', collapseOnScroll);
  }, [mode, suppressed]);

  const dismiss = () => setMode('dismissed');

  return <AnimatePresence>
    {shown && <motion.div
      key="app-download"
      ref={widgetRef}
      className="app-download-widget"
      initial={{ opacity: 0, y: reducedMotion ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reducedMotion ? 0 : 12 }}
      transition={{ duration: reducedMotion ? 0 : .25 }}>
    <motion.aside
      ref={panelRef}
      className="app-download-prompt"
      data-collapsed={collapsed}
      data-compact={compact}
      data-mode={mode}
      aria-labelledby={compact ? undefined : 'app-download-title'}
      aria-label={compact ? 'Aplicativo Unique Ingressos' : undefined}
      initial={false}
      animate={{
        width: collapsed ? 56 : teaser ? 156 : size.width || undefined,
        height: compact ? 56 : size.height || undefined,
        borderRadius: compact ? 18 : 22,
        backgroundColor: compact ? '#1c211c' : '#ffffff',
        boxShadow: compact ? '0 6px 24px #18201826, 0 0px 0px #18201800' : '0 18px 60px #18201826, 0 3px 12px #1820180a',
      }}
      transition={{ duration: reducedMotion ? 0 : .56, ease: [.22, 1, .36, 1] }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') { event.stopPropagation(); dismiss(); }
      }}>
      <motion.div ref={contentRef} className="app-download-prompt__content" aria-hidden={compact}
        style={{ width: size.width || '100%' }} initial={false}
        animate={{ opacity: compact ? 0 : 1, x: reducedMotion || !compact ? 0 : 12,
          visibility: 'visible', transitionEnd: { visibility: compact ? 'hidden' : 'visible' } }}
        transition={{ duration: reducedMotion ? 0 : .16, delay: reducedMotion || compact ? 0 : .16 }}
        onAnimationComplete={restoreControlFocus}>
      <button ref={closeRef} tabIndex={compact ? -1 : undefined} type="button" className="app-download-prompt__close" onClick={dismiss}
        aria-label="Fechar convite para baixar o app" title="Fechar">
        <X size={18} aria-hidden="true" />
      </button>
      <div className="app-download-prompt__heading">
        <div className="app-download-prompt__icon" aria-hidden="true">
          <img src="/images/unique-logo.svg" alt="" width="46" height="20" />
        </div>
        <div>
          <span className="app-download-prompt__eyebrow">Unique no seu celular</span>
          <h2 id="app-download-title">Seus ingressos na palma da mão.</h2>
        </div>
      </div>
      <p>Baixe o app Unique e leve seus eventos com você.</p>
      <div className="app-download-prompt__stores">
        <a tabIndex={compact ? -1 : undefined} href={APP_STORES.appStore} target="_blank" rel="noopener" aria-label="Baixar na App Store (abre em nova aba)">
          <span aria-hidden="true"><AppleIcon size={21} /></span> App Store
        </a>
        <a tabIndex={compact ? -1 : undefined} href={APP_STORES.googlePlay} target="_blank" rel="noopener" aria-label="Baixar no Google Play (abre em nova aba)">
          <span aria-hidden="true"><GooglePlayIcon size={20} /></span> Google Play
        </a>
      </div>
      </motion.div>
      <motion.button ref={teaserRef} type="button" className="app-download-teaser"
        aria-label="Baixar app Unique" aria-expanded={false} aria-hidden={!teaser}
        tabIndex={teaser ? 0 : -1} initial={false}
        animate={{ opacity: teaser ? 1 : 0, x: reducedMotion || teaser ? 0 : 12 }}
        transition={{ duration: reducedMotion ? 0 : .16 }}
        onClick={() => { restoreFocus.current = true; setMode('expanded'); }}>
        <Smartphone size={22} aria-hidden="true" /><span>Baixar app</span>
      </motion.button>
      <motion.button ref={launcherRef} type="button" className="app-download-launcher"
        aria-label="Abrir convite para baixar o app Unique" aria-expanded={false} aria-hidden={!collapsed}
        tabIndex={collapsed ? 0 : -1} title="Baixar o app Unique" initial={false}
        animate={{ opacity: collapsed ? 1 : 0, scale: reducedMotion || collapsed ? 1 : .8 }}
        transition={{ duration: reducedMotion ? 0 : .2, delay: reducedMotion || !collapsed ? 0 : .26 }}
        onClick={() => { restoreFocus.current = true; setMode('expanded'); }}>
        <Smartphone size={23} aria-hidden="true" />
        <span>App</span>
      </motion.button>
    </motion.aside>
    </motion.div>}
  </AnimatePresence>;
}
